/**
 * "Game type" is a second browsing dimension, orthogonal to the asset category
 * taxonomy in `marketplace-taxonomy.ts`: a category answers *what an asset is*
 * (a model, a UI kit), a game type answers *what you are building with it*.
 *
 * Mock products carry no game-type field, so — exactly like
 * `productTaxonomyScope` — the scope is derived deterministically from the
 * product id. This keeps the persisted product contract unchanged while giving
 * every game-type view a stable, non-overlapping slice of the catalog.
 */

export interface GameType {
  slug: string;
  label: string;
  /** Manifest image key for the tile. */
  imageKey: string;
}

export const GAME_TYPES: readonly GameType[] = [
  { slug: "rpg-adventure", label: "RPG & Adventure", imageKey: "p-nature-kit" },
  { slug: "roleplay", label: "Roleplay", imageKey: "p-modern-lobby-kit" },
  { slug: "simulator", label: "Simulator", imageKey: "p-interaction-system" },
  { slug: "tycoon", label: "Tycoon", imageKey: "p-tycoon-kit" },
  { slug: "horror", label: "Horror", imageKey: "c-dusk-village" },
  { slug: "combat", label: "Combat", imageKey: "p-vehicle-pack" },
];

export function isGameTypeSlug(value: string): boolean {
  return GAME_TYPES.some((type) => type.slug === value);
}

export function gameTypeLabel(slug?: string) {
  return GAME_TYPES.find((type) => type.slug === slug)?.label;
}

/** Deterministic string hash (djb2) — stable across runs and platforms. */
function hash(input: string): number {
  let value = 5381;
  for (let index = 0; index < input.length; index++) {
    value = (value * 33) ^ input.charCodeAt(index);
  }
  return value >>> 0;
}

/**
 * Stable game type for a mock product, derived from its id. Hashed rather than
 * taken modulo the product number: `productTaxonomyScope` uses that number
 * directly, so plain arithmetic would correlate the two dimensions (every
 * "Tycoon" product would land in one asset subcategory) instead of crossing them.
 */
export function productGameType(productId: string): string {
  return GAME_TYPES[hash(`game-type:${productId}`) % GAME_TYPES.length]!.slug;
}
